export type SectionContent = Record<string, string>;

export interface AppContent {
  app: {
    brand: string;
    tagline: string;
  };
  home: SectionContent;
  scan: SectionContent;
  inventory: SectionContent;
  profile: SectionContent;
  help: SectionContent;
  pwa: SectionContent;
  auth: SectionContent;
  common: SectionContent;
}

export const en: AppContent = {
  app: {
    brand: "Wandering Sock",
    tagline: "Photos → Searchable Items",
  },
  home: {
    title: "Help",
    hero_title: "Organize Your Life",
    hero_description: "One place for everything you own: organized, searchable, and shareable.",
    feature_scan_title: "Smart Scanning",
    feature_scan_body: "Take a photo and let AI identify your items.",
    feature_organize_title: "Auto-Organize",
    feature_organize_body: "Automatically categorizes and sorts your stuff",
    feature_track_title: "Location Tracking",
    feature_track_body: "Never lose track of where you put things",
    cta_get_started: "Get Started",
    cta_subtitle: "Free to use • No credit card required",
  },
  scan: {
    title: "Add",
    instructions: "Add new items",
    analysis_results_title: "Analysis Results",
    no_items_detected: "No items detected in the image.",
    analysis_error_prefix: "Analysis Error:",
    analysis_completed_footer: "Analysis completed using GPT-4 Vision API",
    button_analyze: "Analyze",
    analyzing: "Analyzing...",
    saving: "Saving...",
    saved: "Saved",
    save_items_prefix: "Save",
    items_suffix: "Items",
    apply_to_selected: "Apply to Selected",
    next: "Next",
    skip: "Skip",
    drop_here_or_click: "Drop an image here or click to select",
    file_types_supported: "Supports: PNG, JPG, JPEG, GIF, BMP, WebP",
    error_analysis_failed: "Failed to analyze image. Please try again.",
    dialog_unsaved_title: "Unsaved Changes",
    dialog_unsaved_body: "You have unsaved items. Are you sure you want to proceed to the next image?",
    dialog_cancel: "Cancel",
    dialog_confirm: "Yes, Continue",
    results_title: "Review Items",
    select_all_label: "Select All",
    bulk_area_placeholder: "Area for selected items",
    bulk_area_apply: "Apply Area",
    bulk_location_placeholder: "Location for selected items",
    bulk_location_apply: "Apply Location",
    table_item: "Item",
    table_description: "Description",
    table_category: "Category",
    table_color: "Color",
    table_area: "Area",
    table_location: "Location",
  },
  inventory: {
    title: "Inventory",
    summary_prefix: "You have",
    items_label: "items",
    empty: "No items yet",
    empty_title: "No items",
    empty_description: "",
    loading_items: "Loading your items...",
    error_load_items: "Failed to load items. Please try again.",
    search_placeholder: "Search your items...",
    all_categories: "All Categories",
  },
  profile: {
    title: "Profile",
    manage_account: "Manage your account and preferences",
    account_type_label: "Account Type:",
    account_type_standard: "Standard",
    account_type_admin: "Administrator",
    user_id_label: "User ID:",
    admin_panel_label: "Admin Panel",
    logout: "Logout",
    settings_title: "Settings",
    settings_loading: "Loading settings…",
    signup_open: "Signup Open",
    invitation_required: "Invitation Required",
    users_title: "Users",
    users_loading: "Loading users…",
    users_table_id: "ID",
    users_table_email: "Email",
    users_table_name: "Name",
    users_table_role: "Role",
    users_table_created: "Created",
    users_table_actions: "Actions",
    users_role_user: "user",
    users_role_admin: "admin",
    users_pagination_prev: "Prev",
    users_pagination_next: "Next",
    users_pagination_page: "Page",
    users_pagination_total: "total",
    users_rows_per_page: "Rows per page",
    invite_title: "Invitation",
    invite_email_placeholder: "email@example.com",
    invite_button_creating: "Creating…",
    invite_button_create: "Invite",
    invite_button_copy: "Copy",
    invite_button_copied: "Copied!",
    invite_share_message: "Share this token with the invited user.",
  },
  help: {
    title: "Help",
    what_is_title: "🔍 Track what matters. Find it fast.",
    what_is_body: "Wandering Sock helps you create a visual inventory of everything that matters to you. Capture your items in photos, and let the app intelligently identify, tag, and organize them. Always know what you have and where they are.",
    quick_start_title: "🚀 Quick Start",
    quick_start_intro: "Get started with Wandering Sock in two simple steps:",
    step1_title: "Add items",
    step1_body: "Select Auto mode and upload or take photos of your things. Wandering Sock will automatically identify, categorize, and store them as items in your inventory. If you prefer, you can also enter items manually.",
    step1_button: "Scan",
    step2_title: "Manage Inventory",
    step2_body: "You can browse, search, edit, and delete items. Organize items in named collections that make sense to you. Special collections like Stock can be used as a grocery list or to manage stock for a small store.",
    step2_button: "Items",
    auto_mode_title: "⚡ Auto Mode",
    auto_mode_intro: "Upload photos and let AI automatically identify, categorize, and save items to your inventory:",
    auto_mode_instant: "Instant Processing: Photos are analyzed and saved automatically as you upload them",
    auto_mode_defaults: "Apply Defaults: Set room, location, and other details once to apply to all items",
    auto_mode_filename: "Filename Parsing: Name files like photo--Living_Room--Coffee_Table.jpg to auto-fill room and location",
    auto_mode_background: "Background Queue: Upload multiple photos and let them process while you continue working",
    semi_auto_mode_title: "🔧 Semi-Auto Mode",
    semi_auto_mode_intro: "Review and edit AI-detected items before saving to your inventory:",
    semi_auto_mode_review: "Review Items: See all detected items in a table with AI-generated details",
    semi_auto_mode_edit: "Edit Before Saving: Modify names, descriptions, categories, or other details",
    semi_auto_mode_bulk_actions: "Bulk Actions: Apply room/location to multiple items at once, or delete unwanted items",
    semi_auto_mode_control: "Full Control: Only save items when you're satisfied with the details",
    manual_mode_title: "✍️ Manual Mode",
    manual_mode_intro: "Add items directly with or without photos for quick entry. Perfect for items that are difficult to photograph or when you want to bypass AI analysis entirely.",
    scan_page_title: "📸 Scan Page",
    scan_page_intro: "The Scan page is where you add new items to your inventory. Choose from three modes:",
    collections_title: "📁 Collections",
    collections_intro: "Organize your items into custom collections for better management:",
    collections_create: "Create collections: Group related items together (e.g., \"Kitchen essentials\", \"Camping gears\")",
    collections_add: "Add items: Select items from your inventory and add them to any collection",
    collections_stock: "Stock keeping mode: Enable stock keeping on a collection to monitor inventory levels",
    collections_checklist: "Checklist mode: Enable checklist mode on a collection to create shopping lists or task lists",
    collections_view: "Manage collections: view and edit items in your custom collections",
    stock_collection_title: "Stock Keeping Collections",
    stock_collection_intro: "Enable stock keeping mode on any collection to track consumable items and get low-stock alerts:",
    stock_collection_enable: "Enable \"Stock keeping\" when creating or editing a collection to activate inventory tracking",
    stock_collection_minimum: "Set minimum quantity levels for items to get notified when they're running low",
    stock_collection_perfect: "Perfect for grocery staples, office supplies, craft materials, and household essentials",
    stock_collection_view: "View stock collections in card or list view to monitor and adjust inventory levels",
    items_page_title: "📦 Items Page",
    items_page_intro: "Your complete inventory management hub. Here you can:",
    items_page_search: "Search: Find items quickly by name or description",
    items_page_filter: "Filter: Narrow down by area, location, category, or color",
    items_page_collections: "View collections: Access your custom collections and Stock collection",
    items_page_edit: "Edit items: Update details, change locations, or modify quantities",
    items_page_photos: "View photos: See the original photo for each item",
    items_page_delete: "Delete items: Move unwanted items to Trash",
    trash_title: "🗑️ Trash",
    trash_intro: "Deleted items are moved to Trash where they can be restored or permanently deleted. This gives you a safety net in case you accidentally delete something important.",
    tips_title: "💡 Tips & Tricks",
    tips_photography: "Photography Tips",
    tips_workflow: "Workflow Tips",
    tips_use_cases: "🧭 Wandering Sock in Action",
    tips_intro: "Get the most out of Wandering Sock:",
    tip_lighting: "Good Lighting: Take photos in well-lit areas for better recognition",
    tip_clear_view: "Clear View: Ensure items are clearly visible and not obscured",
    tip_group_photos: "Group Photos: Photograph multiple items together in one shot - AI will detect and catalog each item separately, saving you time",
    tip_auto_upload: "Auto Upload: Use Auto mode to catalog entire rooms quickly",
    tip_manual_entry: "Manual Entry: Switch to Manual mode to add items with or without photos, bypassing AI analysis for faster entry",
    tip_search: "Search: Use the search bar in Inventory to quickly find items",
    tip_filters: "Filters: Filter by room, location, category, or color",
    search_filter_title: "🔍 Search & Filter",
    search_filter_intro: "In the Inventory tab, you can:",
    search_filter_search: "Search by item name or description",
    search_filter_filter: "Filter by room, location, category, or color",
    search_filter_view: "Click on an item to view details and the original photo",
    search_filter_edit: "Edit or delete items as needed",
    search_filter_same_photo: "View all items from the same photo",
    use_case_moving_title: "📦 Moving to a New Home",
    use_case_moving_body: "Make your next move smoother. Snap photos of your belongings before packing, and label each box by room. Wandering Sock keeps track of what's inside every box, so when it's time to unpack, you can find what you need without opening them all.",
    use_case_home_title: "🏠 Home Organization",
    use_case_home_body: "Keep tabs on everything from seasonal decorations to tools and kitchen gadgets. With Wandering Sock, you'll always know what you have and where it's stored. Perfect for decluttering, staying organized, or keeping records for insurance.",
    use_case_storage_title: "📦 Putting Things in Storage",
    use_case_storage_body: "Store with confidence. Whether you're packing up seasonal clothes, extra furniture, or keepsakes, use Wandering Sock to catalog what goes into each bin or unit. You'll always know what's in storage and exactly where to find it—no digging through boxes required.",
    use_case_stock_title: "📊 Stock Keeping & Inventory Management",
    use_case_stock_body: "Stay on top of your household supplies or small business stock. Track pantry goods, office items, or craft materials with quantities and restock reminders. Wandering Sock helps you see what's running low—so you never run out of the essentials.",
    use_case_insurance_title: "🛡️ Insurance Documentation",
    use_case_insurance_body: "Create a detailed visual record of your valuables. Photograph electronics, furniture, jewelry, and more—Wandering Sock automatically adds descriptions and categories. If you ever need to file a claim, you'll have organized photos and records ready to share with your insurance provider.",
    pro_tips_title: "💡 Pro Tips",
    pro_tip_lighting: "Good Lighting: Take photos in well-lit areas for better AI recognition",
    pro_tip_clear_view: "Clear View: Ensure items are clearly visible and not obscured",
    pro_tip_multiple_angles: "Multiple Angles: For complex items, take photos from different angles",
    pro_tip_bulk_upload: "Auto Upload: Use Auto mode to catalog entire rooms quickly",
    privacy_title: "Your Privacy Matters",
    privacy_body_1: "Your photos and inventory data are private and secure. We don't share your personal identifiable data with third parties. When adding items using Auto and Semi-auto modes, your photos are sent annonymously to AI services for content analysis, so keep that in mind: don't upload any personal identifiable data. If you want to store sensitive items, use Manual mode." ,
    privacy_body_2: "We hope you'll be a part of our community for a long time and we have exciting plans for the future. But if you decide to wander, we'll help you pack your data and take it with you. Your data is always yours.",
    contact_title: "📧 Contact Us",
    contact_question: "Got a question? Lost a sock? We're here to help:",
    contact_question_email: "lostandfound@wanderingsock.com",
    contact_suggestion: "Have a suggestion? We would love to hear it:",
    contact_suggestion_email: "tinkeringsock@wanderingsock.com",
  },
  pwa: {
    install_title: "📱 Install Wandering Sock App",
    ios_message: "Add to your home screen for the best experience!",
    other_message: "Get quick access and offline features!",
    btn_how_to_install: "How to Install",
    btn_install: "Install",
    ios_alert_instructions: "To add this app to your home screen:\n\n1. Tap the Share button (square with arrow) in Safari\n2. Scroll down and tap \"Add to Home Screen\"\n3. Tap \"Add\" to confirm",
  },
  auth: {
    login: "Log in",
    logout: "Log out",
    signup: "Create account",
    or: "or",
    email_label: "Email",
    email_placeholder: "Enter your email",
    password_label: "Password",
    password_placeholder: "Enter your password",
    confirm_password_label: "Confirm Password",
    confirm_password_placeholder: "Confirm your password",
    display_name_label: "Display Name",
    display_name_placeholder: "Enter your display name",
    sign_in_title: "Sign In",
    sign_in_button: "Sign In",
    signing_in: "Signing In...",
    dont_have_account: "Don't have an account?",
    sign_up_link: "Sign up",
    forgot_password_link: "Forgot your password?",
    login_failed: "Login failed",
    google_login_failed: "Google login failed",
    sign_in_with_google: "Sign in with Google",
    create_account_title: "Create Account",
    create_account_button: "Create Account",
    creating_account: "Creating Account...",
    already_have_account: "Already have an account?",
    sign_in_link: "Sign in",
    google_sign_up: "Sign up with Google",
    registration_failed: "Registration failed",
    google_registration_failed: "Google registration failed",
    password_mismatch: "Passwords do not match",
    password_too_short: "Password must be at least 8 characters long",
    display_name_required: "Display name is required",
    reset_password_title: "Reset Password",
    email_address_label: "Email Address",
    email_address_placeholder: "Enter your email address",
    send_reset_link: "Send Reset Link",
    sending: "Sending...",
    reset_instructions_sent: "Password reset instructions have been sent to your email.",
    back_to_sign_in: "Back to Sign In",
    forgot_failed: "Failed to send reset email",
    network_error_try_again: "Network error. Please try again.",
    set_new_password_title: "Set New Password",
    setting_new_password_for: "Setting new password for:",
    new_password_label: "New Password",
    new_password_placeholder: "Enter new password",
    confirm_new_password_label: "Confirm New Password",
    confirm_new_password_placeholder: "Confirm new password",
    updating_password: "Updating Password...",
    update_password: "Update Password",
    reset_failed: "Failed to reset password",
    invalid_reset_link: "Invalid reset link. Please request a new password reset.",
    reset_page_title: "Reset Your Password",
    reset_page_subtitle: "Enter your new password below.",
    resetting: "Resetting...",
    reset_password_button: "Reset Password",
    back_to_login: "Back to Login",
    reset_success_title: "Password Reset Successful",
    reset_success_body: "Your password has been successfully reset. You will be redirected to the login page in a few seconds.",
    go_to_login: "Go to Login",
  },
  common: {
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    delete_selected: "Delete Selected",
    close: "Close",
    retry: "Try Again",
    auth_required: "Authentication required",
    error_prefix: "Error:",
    save_failed: "Failed to save items. Please try again.",
    update_failed: "Failed to update items. Please try again.",
    saved_success_prefix: "Successfully saved",
    updated_success_prefix: "Successfully updated",
    items_exclamation: "items!",
  },
};
